// Lightweight, dependency-free SVG charts for the report engine. Each chart
// takes a normalized series shape and renders a responsive viewBox SVG that
// follows the theme via CSS variables / a fixed palette. Deliberately small —
// bar (grouped), line (multi-series), and pie — enough to visualize the report
// kinds without pulling in recharts/d3.

export interface ChartDatum {
  label: string;
  values: { key: string; value: number }[];
}

const PALETTE = ["#10b981", "#3b82f6", "#f59e0b", "#8b5cf6", "#ef4444", "#14b8a6", "#ec4899", "#64748b"];
const fmt = (n: number) => (Number.isInteger(n) ? n.toLocaleString() : n.toLocaleString(undefined, { maximumFractionDigits: 2 }));
/** Coerce a series value to a finite number — a non-numeric/NaN value would
 *  otherwise poison `max` (Math.max → NaN) and blank the whole chart. */
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function Legend({ keys }: { keys: string[] }) {
  if (keys.length < 2) return null;
  return (
    <div className="chart-legend">
      {keys.map((k, i) => (
        <span key={k} className="chart-legend-item">
          <span className="chart-swatch" style={{ background: PALETTE[i % PALETTE.length] }} />
          {k.replace(/_/g, " ")}
        </span>
      ))}
    </div>
  );
}

/** Grouped vertical bar chart. One group per datum, one bar per series key. */
export function BarChart({ data, seriesKeys }: { data: ChartDatum[]; seriesKeys: string[] }) {
  const W = 720;
  const H = 320;
  const padL = 44;
  const padB = 64;
  const padT = 12;
  const padR = 12;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...data.flatMap((d) => d.values.map((v) => num(v.value))));
  const groupW = plotW / Math.max(1, data.length);
  const barGap = 4;
  const barW = Math.max(2, (groupW - barGap * (seriesKeys.length + 1)) / Math.max(1, seriesKeys.length));
  const ticks = 4;

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img" aria-label="Bar chart">
        {/* y gridlines + labels */}
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const y = padT + (plotH * i) / ticks;
          const val = Math.round((max * (ticks - i)) / ticks);
          return (
            <g key={i}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} className="chart-grid" />
              <text x={padL - 6} y={y + 4} className="chart-axis" textAnchor="end">{fmt(val)}</text>
            </g>
          );
        })}
        {/* bars */}
        {data.map((d, gi) => {
          const gx = padL + groupW * gi;
          return (
            <g key={gi}>
              {d.values.map((v, si) => {
                const h = (num(v.value) / max) * plotH;
                const x = gx + barGap + si * (barW + barGap);
                const y = padT + plotH - h;
                return (
                  <rect
                    key={v.key}
                    x={x}
                    y={y}
                    width={barW}
                    height={Math.max(0, h)}
                    rx={2}
                    fill={PALETTE[si % PALETTE.length]}
                  >
                    <title>{`${d.label} · ${v.key}: ${fmt(v.value)}`}</title>
                  </rect>
                );
              })}
              <text
                x={gx + groupW / 2}
                y={H - padB + 16}
                className="chart-axis"
                textAnchor="end"
                transform={`rotate(-35 ${gx + groupW / 2} ${H - padB + 16})`}
              >
                {d.label.length > 16 ? d.label.slice(0, 15) + "…" : d.label}
              </text>
            </g>
          );
        })}
      </svg>
      <Legend keys={seriesKeys} />
    </div>
  );
}

/** Multi-series line chart over an ordered category axis. */
export function LineChart({ data, seriesKeys }: { data: ChartDatum[]; seriesKeys: string[] }) {
  const W = 720;
  const H = 320;
  const padL = 44;
  const padB = 64;
  const padT = 12;
  const padR = 12;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const max = Math.max(1, ...data.flatMap((d) => d.values.map((v) => num(v.value))));
  const n = Math.max(1, data.length - 1);
  const x = (i: number) => padL + (plotW * i) / n;
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  const ticks = 4;
  // Thin out x labels when crowded.
  const labelEvery = Math.ceil(data.length / 10);

  return (
    <div className="chart-wrap">
      <svg viewBox={`0 0 ${W} ${H}`} className="chart-svg" role="img" aria-label="Line chart">
        {Array.from({ length: ticks + 1 }, (_, i) => {
          const yy = padT + (plotH * i) / ticks;
          const val = Math.round((max * (ticks - i)) / ticks);
          return (
            <g key={i}>
              <line x1={padL} y1={yy} x2={W - padR} y2={yy} className="chart-grid" />
              <text x={padL - 6} y={yy + 4} className="chart-axis" textAnchor="end">{fmt(val)}</text>
            </g>
          );
        })}
        {seriesKeys.map((key, si) => {
          const pts = data.map((d, i) => `${x(i)},${y(num(d.values.find((v) => v.key === key)?.value ?? 0))}`).join(" ");
          return (
            <g key={key}>
              <polyline points={pts} fill="none" stroke={PALETTE[si % PALETTE.length]} strokeWidth={2.25} />
              {data.map((d, i) => (
                <circle key={i} cx={x(i)} cy={y(num(d.values.find((v) => v.key === key)?.value ?? 0))} r={2.5} fill={PALETTE[si % PALETTE.length]}>
                  <title>{`${d.label} · ${key}: ${fmt(d.values.find((v) => v.key === key)?.value ?? 0)}`}</title>
                </circle>
              ))}
            </g>
          );
        })}
        {data.map((d, i) =>
          i % labelEvery === 0 ? (
            <text
              key={i}
              x={x(i)}
              y={H - padB + 16}
              className="chart-axis"
              textAnchor="end"
              transform={`rotate(-35 ${x(i)} ${H - padB + 16})`}
            >
              {d.label.length > 16 ? d.label.slice(0, 15) + "…" : d.label}
            </text>
          ) : null,
        )}
      </svg>
      <Legend keys={seriesKeys} />
    </div>
  );
}

/** Pie chart of a single value series across categories. */
export function PieChart({ data, valueKey }: { data: ChartDatum[]; valueKey: string }) {
  const slices = data
    .map((d, i) => ({ label: d.label, value: num(d.values.find((v) => v.key === valueKey)?.value ?? 0), color: PALETTE[i % PALETTE.length] }))
    .filter((s) => s.value > 0);
  const total = slices.reduce((sum, s) => sum + s.value, 0) || 1;
  const R = 130;
  const cx = 150;
  const cy = 150;
  let acc = 0;
  const arc = (frac: number) => {
    const a = 2 * Math.PI * frac - Math.PI / 2;
    return [cx + R * Math.cos(a), cy + R * Math.sin(a)];
  };

  return (
    <div className="chart-wrap pie">
      <svg viewBox="0 0 300 300" className="chart-svg pie-svg" role="img" aria-label="Pie chart">
        {slices.map((s) => {
          const start = acc / total;
          acc += s.value;
          const end = acc / total;
          const [x1, y1] = arc(start);
          const [x2, y2] = arc(end);
          const large = end - start > 0.5 ? 1 : 0;
          const path = `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
          return (
            <path key={s.label} d={path} fill={s.color} stroke="var(--surface, #fff)" strokeWidth={1.5}>
              <title>{`${s.label}: ${fmt(s.value)} (${Math.round((s.value / total) * 100)}%)`}</title>
            </path>
          );
        })}
      </svg>
      <div className="chart-legend col">
        {slices.map((s) => (
          <span key={s.label} className="chart-legend-item">
            <span className="chart-swatch" style={{ background: s.color }} />
            {s.label} · <strong>{fmt(s.value)}</strong> ({Math.round((s.value / total) * 100)}%)
          </span>
        ))}
      </div>
    </div>
  );
}
